<template>
  <div class="base-date-picker">
    <input
      v-bind="mergedAttrs"
      type="text"
      :class="['form-control', mergedAttrs.class, inputClass]"
      :value="displayValue"
      :placeholder="placeholder"
      readonly
      @click="openPicker"
    />
    <div v-if="showPicker" class="date-picker-overlay" @click.self="closePicker">
      <div class="date-picker-modal">
        <div class="date-picker-header">
          <div class="date-picker-year">{{ headerYear }}</div>
          <div class="date-picker-title">{{ headerTitle }}</div>
        </div>
        <div class="date-picker-body">
          <div class="date-picker-nav">
            <button type="button" class="nav-btn" @click="changeMonth(-1)" aria-label="Previous month">
              <span>&lt;</span>
            </button>
            <div class="month-label">{{ currentMonthLabel }}</div>
            <button type="button" class="nav-btn" @click="changeMonth(1)" aria-label="Next month">
              <span>&gt;</span>
            </button>
          </div>
          <div class="weekday-row">
            <span v-for="d in weekdays" :key="d">{{ d }}</span>
          </div>
          <div class="days-grid">
            <button
              v-for="(day, idx) in daysGrid"
              :key="idx"
              type="button"
              class="day-cell"
              :class="{
                empty: !day,
                selected: isSelected(day),
                disabled: isDisabled(day)
              }"
              :disabled="!day || isDisabled(day)"
              @click="selectDay(day)"
            >
              {{ day ? day.getDate() : '' }}
            </button>
          </div>
        </div>
        <div class="date-picker-actions">
          <button type="button" class="btn btn-link text-secondary text-decoration-none" @click="closePicker">Cancel</button>
          <button type="button" class="btn btn-link text-primary text-decoration-none" @click="applyPicker">OK</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { addMonths, endOfMonth, format, isBefore, isAfter, startOfMonth } from 'date-fns'

export default {
  name: 'BaseDatePicker',
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, Date, Number],
      default: ''
    },
    format: {
      type: String,
      default: 'yyyy-MM-dd'
    },
    minDatetime: {
      type: [String, Date],
      default: null
    },
    maxDatetime: {
      type: [String, Date],
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    inputClass: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'input'],
  data () {
    return {
      showPicker: false,
      tempDate: null,
      viewDate: null,
      weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    }
  },
  computed: {
    mergedAttrs () {
      const { class: className, ...attrs } = this.$attrs
      return {
        class: className,
        ...attrs
      }
    },
    minDate () {
      return this.parseDate(this.minDatetime)
    },
    maxDate () {
      return this.parseDate(this.maxDatetime)
    },
    displayValue () {
      if (!this.modelValue) return ''
      const date = this.parseDate(this.modelValue)
      if (!date) return ''
      return format(date, 'yyyy-LL-dd')
    },
    headerYear () {
      return this.tempDate ? format(this.tempDate, 'yyyy') : ''
    },
    headerTitle () {
      return this.tempDate ? format(this.tempDate, 'MMMM d') : ''
    },
    currentMonthLabel () {
      return this.viewDate ? format(this.viewDate, 'MMMM yyyy') : ''
    },
    daysGrid () {
      if (!this.viewDate) return []
      const start = startOfMonth(this.viewDate)
      const end = endOfMonth(this.viewDate)
      const startWeekday = (start.getDay() + 6) % 7 // Monday = 0
      const days = []
      for (let i = 0; i < startWeekday; i++) {
        days.push(null)
      }
      for (let d = 1; d <= end.getDate(); d++) {
        days.push(new Date(start.getFullYear(), start.getMonth(), d))
      }
      return days
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler (val) {
        let base = this.parseDate(val) || new Date()
        if (this.minDate && isBefore(base, this.minDate)) {
          base = new Date(this.minDate)
        }
        this.tempDate = base
        this.viewDate = startOfMonth(this.tempDate)
      }
    }
  },
  methods: {
    parseDate (value) {
      if (!value) return null
      if (value instanceof Date) return new Date(value)
      if (typeof value === 'number') return new Date(value)
      // Plain date string (YYYY-MM-DD): parse as local midnight so the picker
      // shows the date the user actually chose, regardless of timezone.
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-').map(Number)
        return new Date(y, m - 1, d)
      }
      const date = new Date(value)
      return isNaN(date.getTime()) ? null : date
    },
    openPicker () {
      let current = this.parseDate(this.modelValue) || new Date()
      if (this.minDate && isBefore(current, this.minDate)) {
        current = new Date(this.minDate)
      }
      this.tempDate = current
      this.viewDate = startOfMonth(current)
      this.showPicker = true
    },
    closePicker () {
      this.showPicker = false
    },
    applyPicker () {
      if (this.tempDate) {
        // Emit a plain YYYY-MM-DD string (the local calendar date the user chose)
        // instead of a UTC ISO string, so the date is timezone-independent.
        const dateStr = format(this.tempDate, 'yyyy-MM-dd')
        this.$emit('update:modelValue', dateStr)
        this.$emit('input', dateStr)
      }
      this.closePicker()
    },
    changeMonth (delta) {
      this.viewDate = addMonths(this.viewDate, delta)
    },
    selectDay (day) {
      if (!day || this.isDisabled(day)) return
      this.tempDate = day
    },
    isDisabled (day) {
      if (!day) return true
      if (this.minDate && isBefore(day, this.minDate)) return true
      if (this.maxDate && isAfter(day, this.maxDate)) return true
      return false
    },
    isSelected (day) {
      if (!day || !this.tempDate) return false
      return format(day, 'yyyy-LL-dd') === format(this.tempDate, 'yyyy-LL-dd')
    },
    isToday (day) {
      if (!day) return false
      const today = new Date()
      return format(day, 'yyyy-LL-dd') === format(today, 'yyyy-LL-dd')
    }

  }
}
</script>

<style scoped>
.date-picker-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.date-picker-modal {
  background: #fff;
  border-radius: 6px;
  width: 320px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.date-picker-header {
  background: #007d8f;
  color: #fff;
  padding: 18px 24px 14px;
}

.date-picker-year {
  font-size: 14px;
  font-weight: 300;
  opacity: 0.7;
}

.date-picker-title {
  color: #fff;
  font-size: 32px;
  font-family: Rubik, sans-serif;
  font-weight: 400;
  line-height: 1.2;
}

.date-picker-body {
  padding: 12px 12px 4px;
  color: #000;
}

.date-picker-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.month-label {
  font-size: 16px;
  font-weight: 500;
}

.nav-btn {
  background: transparent;
  border: none;
  padding: 4px 8px;
  font-size: 16px;
  cursor: pointer;
}

.weekday-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-weight: 700;
  font-size: 13px;
  margin-bottom: 8px;
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.day-cell {
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 18px;
  cursor: pointer;
  color: #000;
  font-size: 15px;
  font-weight: 300;
  line-height: 36px;
  text-align: center;
}

.day-cell:hover:not(.disabled):not(.empty) {
  background: rgba(0, 128, 128, 0.15);
}

.day-cell.selected {
  background: #007d8f;
  color: #fff;
  font-weight: 400;
}

.day-cell.disabled {
  color: #ccc;
  cursor: not-allowed;
}

.day-cell.empty {
  cursor: default;
}

.date-picker-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 8px 12px 12px;
}

.date-picker-actions .btn {
  font-size: 16px;
  font-weight: 400;
  font-family: Rubik, sans-serif;
}
</style>
